import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import type {
    NCoreUIKitIcon
} from "ncore-ui-kit";
import {
    NCoreUIKitTheme,
    Breadcrumb,
    Sidebar,
    Avatar,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    ChevronRightIcon,
    CheckIcon,
    PlusIcon,
    HomeIcon,
    FileIcon,
    UserIcon,
    InfoIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Sidebar";

const USAGE_CODE = `<Sidebar.Provider
    isDefaultOpen={true}
>
    <Sidebar
        collapseBehaviour="icon"
        variant="sidebar"
    >
        <Sidebar.Header>
            <Sidebar.MenuButton
                subTitle="Workspace"
                icon={StarIcon}
                title="NCore"
            />
        </Sidebar.Header>
        <Sidebar.Content>
            <Sidebar.Group
                title="General"
            >
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            icon={CheckIcon}
                            isActive={true}
                            title="Tasks"
                        />
                        <Sidebar.MenuBadge
                            count={12}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Group>
        </Sidebar.Content>
        <Sidebar.Footer>
            ...
        </Sidebar.Footer>
        <Sidebar.Rail/>
    </Sidebar>
    <Sidebar.Inset>
        <Sidebar.Trigger/>
        ...
    </Sidebar.Inset>
</Sidebar.Provider>`;

const ITEMS_CODE = `<Sidebar.Menu
    items={[
        {
            onPress: () => setPage("tasks"),
            icon: CheckIcon,
            isActive: true,
            title: "Tasks",
            key: "tasks",
            count: 12
        },
        {
            items: [
                {
                    title: "Weekly",
                    key: "weekly"
                }
            ],
            title: "Reports",
            icon: FileIcon,
            key: "reports"
        }
    ]}
/>`;

const STATE_CODE = `const SidebarStatus = () => {
    const {
        handleToggle,
        isPhone,
        state
    } = Sidebar.useContext();

    return <Button
        title={\`\${state} (\${isPhone ? "phone" : "desktop"})\`}
        onPress={handleToggle}
    />;
};

<Sidebar.Provider
    onOpenChange={setIsOpen}
    isOpen={isOpen}
>
    ...
    <Sidebar.Inset>
        <SidebarStatus/>
    </Sidebar.Inset>
</Sidebar.Provider>`;

const BOARD_CODE = `<Sidebar.Provider
    isOpen={isOpen}
    onOpenChange={setIsOpen}
>
    <Sidebar
        collapseBehaviour="icon"
        title="Main navigation"
    >
        <Sidebar.Header>
            <Sidebar.MenuButton
                renderIcon={renderBrandMark}
                subTitle="Pro plan"
                title="NIBGAT"
            />
            <Sidebar.Input
                shortcut={["mod", "k"]}
                aria-label="Search"
                placeholder="Search"
            />
        </Sidebar.Header>
        <Sidebar.Content>
            <Sidebar.Group
                title="Platform"
            >
                <Sidebar.Menu>
                    <Sidebar.MenuItem
                        isDefaultExpanded={true}
                    >
                        <Sidebar.MenuButton
                            title="Projects"
                            icon={FileIcon}
                        />
                        <Sidebar.MenuSub>
                            <Sidebar.MenuSubItem>
                                <Sidebar.MenuSubButton
                                    title="Analytics Engine"
                                    isActive={true}
                                />
                                <Sidebar.MenuBadge
                                    count={12}
                                />
                            </Sidebar.MenuSubItem>
                        </Sidebar.MenuSub>
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
                <Sidebar.MenuButton
                    title="New project"
                    variant="outline"
                    icon={PlusIcon}
                />
            </Sidebar.Group>
        </Sidebar.Content>
    </Sidebar>
    <Sidebar.Inset>
        ...
    </Sidebar.Inset>
</Sidebar.Provider>`;

const INPUT_CODE = `<Sidebar.Input
    onChangeText={setQuery}
    shortcut={["mod", "k"]}
    aria-label="Search"
    placeholder="Search"
/>`;

const GROUP_CODE = `<Sidebar.Group>
    <Sidebar.GroupLabel>
        Settings
        <Sidebar.GroupAction
            onPress={handleAddSetting}
            title="Add setting"
        />
    </Sidebar.GroupLabel>
    <Sidebar.GroupContent>
        <Sidebar.Menu>
            ...
        </Sidebar.Menu>
    </Sidebar.GroupContent>
</Sidebar.Group>`;

const SUB_ITEM_CODE = `<Sidebar.MenuSub>
    <Sidebar.MenuSubItem>
        <Sidebar.MenuSubButton
            title="Analytics Engine"
            isActive={true}
        />
        <Sidebar.MenuBadge
            count={12}
        />
    </Sidebar.MenuSubItem>
</Sidebar.MenuSub>`;

const NESTED_CODE = `<Sidebar.MenuItem
    isCollapsible={false}
>
    <Sidebar.MenuButton
        title="Workspace"
        icon={StarIcon}
    />
    <Sidebar.MenuSub>
        <Sidebar.MenuItem
            isDefaultExpanded={true}
        >
            <Sidebar.MenuButton
                title="Releases"
                icon={FileIcon}
                size="medium"
            />
            <Sidebar.MenuSub>
                <Sidebar.MenuSubItem>
                    <Sidebar.MenuSubButton
                        title="Notes"
                    />
                </Sidebar.MenuSubItem>
            </Sidebar.MenuSub>
        </Sidebar.MenuItem>
        <Sidebar.MenuItem>
            <Sidebar.MenuButton
                endIcon={ChevronRightIcon}
                title="Archive"
                icon={InfoIcon}
                size="medium"
            />
        </Sidebar.MenuItem>
    </Sidebar.MenuSub>
</Sidebar.MenuItem>`;

const OUTLINE_CODE = `<Sidebar.MenuButton
    title="New project"
    variant="outline"
    icon={PlusIcon}
/>`;

type SidebarCollapseBehaviour = "offcanvas" | "icon" | "none";

type SidebarVariant = "floating" | "sidebar" | "inset";

type ShellOptions = {
    collapseBehaviour?: SidebarCollapseBehaviour;
    onOpenChange?: (isOpen: boolean) => void;
    isShortcutEnabled?: boolean;
    height?: "short" | "tall";
    variant?: SidebarVariant;
    isStateVisible?: boolean;
    isTeamVisible?: boolean;
    isDefaultOpen?: boolean;
    isOpen?: boolean;
    id: string;
};

const COLLAPSE_BEHAVIOURS = [
    "icon",
    "offcanvas",
    "none"
] as const;

const VARIANTS = [
    "sidebar",
    "floating",
    "inset"
] as const;

const STAGE_COMPACT_BREAKPOINT = 0;

const stylesheet = StyleSheet.create({
    mark: {
        justifyContent: "center",
        alignItems: "center"
    },
    inset: {
        alignItems: "center",
        flexDirection: "row"
    },
    stage: {
        flexDirection: "row",
        height: "100%",
        width: "100%"
    },
    cell: {
        width: "100%"
    }
});

const StageFrame = ({
    children,
    height
}: {
    height: "short" | "tall";
    children: ReactNode;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return <DemoFrame
        height={height === "tall" ? shapeConfigs.contentMaxWidth / 2 : shapeConfigs.chartHeight.large}
    >
        <View
            style={stylesheet.stage}
        >
            {children}
        </View>
    </DemoFrame>;
};

const InsetBar = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.inset,
            {
                padding: spaces.spacingMd,
                gap: spaces.spacingSm
            }
        ]}
    >
        {children}
    </View>;
};

const Cell = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.cell,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        {children}
    </View>;
};

const BrandMark = ({
    borderRadius,
    size
}: {
    borderRadius: number;
    size: number;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.mark,
            {
                backgroundColor: colors.content.container.mid,
                borderRadius,
                height: size,
                width: size
            }
        ]}
    >
        <Text
            variant="labelLargeSize"
            color="high"
        >
            N
        </Text>
    </View>;
};

const StateNote = ({
    label
}: {
    label: string;
}) => {
    const {
        state
    } = Sidebar.useContext();

    return <Text
        variant="labelSmallSize"
        color="mid"
    >
        {`${label}: ${state}`}
    </Text>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("sidebarPage.collapseTitle"),
        id: "collapse"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("sidebarPage.itemsTitle"),
        id: "items"
    },
    {
        title: t("sidebarPage.stateTitle"),
        id: "state"
    },
    {
        title: t("sidebarPage.boardTitle"),
        id: "board"
    },
    {
        title: t("sidebarPage.inputTitle"),
        id: "input"
    },
    {
        title: t("sidebarPage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("sidebarPage.subItemsTitle"),
        id: "sub-items"
    },
    {
        title: t("sidebarPage.nestedTitle"),
        id: "nested"
    },
    {
        title: t("sidebarPage.outlineTitle"),
        id: "outline"
    },
    {
        title: t("sidebarPage.railBadgesTitle"),
        id: "rail-badges"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SidebarPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        activePage,
        setActivePage
    ] = useState("tasks");

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const [
        isOpen,
        setIsOpen
    ] = useState(true);

    const [
        isBoardOpen,
        setIsBoardOpen
    ] = useState(true);

    const [
        boardPage,
        setBoardPage
    ] = useState("analytics");

    const pageTitles: Record<string, string> = {
        dashboard: t("sidebarPage.itemDashboard"),
        members: t("sidebarPage.itemMembers"),
        monthly: t("sidebarPage.subMonthly"),
        weekly: t("sidebarPage.subWeekly"),
        tasks: t("sidebarPage.itemTasks")
    };

    const currentTitle = pageTitles[activePage] ?? activePage;

    const handlePage = (page: string) => () => setActivePage(page);

    const handleAction = (label: string) => () => setLastAction(label);

    const handleOpenSwitch = () => setIsOpen(!isOpen);

    const handleBoardRailSwitch = () => setIsBoardOpen(!isBoardOpen);

    const boardTitles: Record<string, string> = {
        integrations: t("sidebarPage.boardIntegrations"),
        analytics: t("sidebarPage.boardAnalytics"),
        dashboard: t("sidebarPage.boardDashboard"),
        billing: t("sidebarPage.boardBilling"),
        compass: t("sidebarPage.boardCompass"),
        general: t("sidebarPage.boardGeneral"),
        sonar: t("sidebarPage.boardSonar"),
        team: t("sidebarPage.boardTeam")
    };

    const boardCurrentTitle = boardTitles[boardPage] ?? boardPage;

    const handleBoardPage = (page: string) => () => setBoardPage(page);

    const renderBrandMark = ({
        borderRadius,
        size
    }: {
        borderRadius: number;
        size: number;
    }) => <BrandMark
        borderRadius={borderRadius}
        size={size}
    />;

    const renderUserAvatar = () => <Avatar
        title={t("sidebarPage.boardUserName")}
        size="small"
    />;

    const renderBoardMenuItem = (key: string, icon: NCoreUIKitIcon) => <Sidebar.MenuItem
        key={key}
    >
        <Sidebar.MenuButton
            title={boardTitles[key] ?? key}
            onPress={handleBoardPage(key)}
            isActive={boardPage === key}
            icon={icon}
        />
    </Sidebar.MenuItem>;

    const renderBoardSub = (key: string, count: number) => <Sidebar.MenuSubItem
        key={key}
    >
        <Sidebar.MenuSubButton
            title={boardTitles[key] ?? key}
            onPress={handleBoardPage(key)}
            isActive={boardPage === key}
        />
        <Sidebar.MenuBadge
            count={count}
        />
    </Sidebar.MenuSubItem>;

    const renderBoard = () => <StageFrame
        height="tall"
    >
        <Sidebar.Provider
            compactBreakpoint={STAGE_COMPACT_BREAKPOINT}
            onOpenChange={setIsBoardOpen}
            isOpen={isBoardOpen}
            shortcut={null}
        >
            <Sidebar
                title={t("sidebarPage.navTitle")}
                collapseBehaviour="icon"
            >
                <Sidebar.Header>
                    <Sidebar.MenuButton
                        subTitle={t("sidebarPage.boardPlan")}
                        title={t("sidebarPage.boardBrand")}
                        renderIcon={renderBrandMark}
                    />
                    <Sidebar.Input
                        placeholder={t("sidebarPage.boardSearch")}
                        aria-label={t("sidebarPage.boardSearch")}
                        shortcut={[
                            "mod",
                            "k"
                        ]}
                    />
                </Sidebar.Header>
                <Sidebar.Content>
                    <Sidebar.Group
                        title={t("sidebarPage.boardPlatform")}
                    >
                        <Sidebar.Menu>
                            {renderBoardMenuItem("dashboard", HomeIcon)}
                            <Sidebar.MenuItem
                                isDefaultExpanded={true}
                            >
                                <Sidebar.MenuButton
                                    title={t("sidebarPage.boardProjects")}
                                    icon={FileIcon}
                                />
                                <Sidebar.MenuSub>
                                    {renderBoardSub("analytics", 12)}
                                    {renderBoardSub("sonar", 4)}
                                    {renderBoardSub("compass", 7)}
                                </Sidebar.MenuSub>
                            </Sidebar.MenuItem>
                            <Sidebar.MenuItem>
                                <Sidebar.MenuButton
                                    title={t("sidebarPage.boardTeam")}
                                    onPress={handleBoardPage("team")}
                                    isActive={boardPage === "team"}
                                    icon={UserIcon}
                                />
                                <Sidebar.MenuBadge
                                    count={3}
                                />
                            </Sidebar.MenuItem>
                        </Sidebar.Menu>
                        <Sidebar.MenuButton
                            title={t("sidebarPage.boardNewProject")}
                            variant="outline"
                            icon={PlusIcon}
                        />
                    </Sidebar.Group>
                    <Sidebar.Group>
                        <Sidebar.GroupLabel>
                            {t("sidebarPage.boardSettings")}
                            <Sidebar.GroupAction
                                title={t("sidebarPage.boardAddSetting")}
                                icon={PlusIcon}
                            />
                        </Sidebar.GroupLabel>
                        <Sidebar.GroupContent>
                            <Sidebar.Menu>
                                {renderBoardMenuItem("general", InfoIcon)}
                                {renderBoardMenuItem("billing", CheckIcon)}
                                {renderBoardMenuItem("integrations", StarIcon)}
                            </Sidebar.Menu>
                        </Sidebar.GroupContent>
                    </Sidebar.Group>
                </Sidebar.Content>
                <Sidebar.Footer>
                    <Sidebar.MenuButton
                        subTitle={t("sidebarPage.boardUserMail")}
                        title={t("sidebarPage.boardUserName")}
                        renderIcon={renderUserAvatar}
                    />
                </Sidebar.Footer>
                <Sidebar.Rail/>
            </Sidebar>
            <Sidebar.Inset>
                <InsetBar>
                    <Sidebar.Trigger/>
                    <Breadcrumb>
                        <Breadcrumb.List>
                            <Breadcrumb.Item>
                                <Breadcrumb.Link
                                    title={t("sidebarPage.boardProjects")}
                                    onPress={handleBoardPage("analytics")}
                                />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Breadcrumb.Page
                                    title={boardCurrentTitle}
                                />
                            </Breadcrumb.Item>
                        </Breadcrumb.List>
                    </Breadcrumb>
                </InsetBar>
                <InsetBar>
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {boardCurrentTitle}
                    </Text>
                </InsetBar>
            </Sidebar.Inset>
        </Sidebar.Provider>
    </StageFrame>;

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    const renderCell = (label: string, node: ReactNode) => <Cell
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </Cell>;

    const renderGroups = (isTeamVisible: boolean) => <>
        <Sidebar.Group
            title={t("sidebarPage.groupGeneral")}
        >
            <Sidebar.Menu>
                <Sidebar.MenuItem>
                    <Sidebar.MenuButton
                        title={t("sidebarPage.itemDashboard")}
                        isActive={activePage === "dashboard"}
                        onPress={handlePage("dashboard")}
                        icon={HomeIcon}
                    />
                </Sidebar.MenuItem>
                <Sidebar.MenuItem>
                    <Sidebar.MenuButton
                        title={t("sidebarPage.itemTasks")}
                        isActive={activePage === "tasks"}
                        onPress={handlePage("tasks")}
                        icon={CheckIcon}
                    />
                    <Sidebar.MenuBadge
                        label={t("sidebarPage.badgeOpen")}
                        count={12}
                    />
                </Sidebar.MenuItem>
                <Sidebar.MenuItem>
                    <Sidebar.MenuButton
                        title={t("sidebarPage.itemReports")}
                        icon={FileIcon}
                    />
                    <Sidebar.MenuSub>
                        <Sidebar.MenuSubButton
                            isActive={activePage === "weekly"}
                            title={t("sidebarPage.subWeekly")}
                            onPress={handlePage("weekly")}
                        />
                        <Sidebar.MenuSubButton
                            isActive={activePage === "monthly"}
                            title={t("sidebarPage.subMonthly")}
                            onPress={handlePage("monthly")}
                        />
                    </Sidebar.MenuSub>
                </Sidebar.MenuItem>
            </Sidebar.Menu>
        </Sidebar.Group>
        {
            isTeamVisible ?
                <Sidebar.Group
                    title={t("sidebarPage.groupTeam")}
                >
                    <Sidebar.Menu>
                        <Sidebar.MenuItem>
                            <Sidebar.MenuButton
                                title={t("sidebarPage.itemMembers")}
                                isActive={activePage === "members"}
                                onPress={handlePage("members")}
                                icon={UserIcon}
                            />
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.Group>
            :
                null
        }
    </>;

    const renderShell = ({
        collapseBehaviour = "icon",
        isShortcutEnabled = false,
        isStateVisible = false,
        isDefaultOpen = true,
        isTeamVisible = true,
        variant = "sidebar",
        isOpen: isOpenProp,
        height = "tall",
        onOpenChange,
        id
    }: ShellOptions) => <StageFrame
        height={height}
        key={id}
    >
        <Sidebar.Provider
            shortcut={isShortcutEnabled ? undefined : null}
            compactBreakpoint={STAGE_COMPACT_BREAKPOINT}
            isDefaultOpen={isDefaultOpen}
            onOpenChange={onOpenChange}
            isOpen={isOpenProp}
            variant={variant}
        >
            <Sidebar
                collapseBehaviour={collapseBehaviour}
                title={t("sidebarPage.navTitle")}
            >
                <Sidebar.Header>
                    <Sidebar.MenuButton
                        subTitle={t("sidebarPage.workspace")}
                        title={t("sample.brand")}
                        icon={StarIcon}
                    />
                </Sidebar.Header>
                <Sidebar.Content>
                    {renderGroups(isTeamVisible)}
                </Sidebar.Content>
                <Sidebar.Footer>
                    <Sidebar.Menu>
                        <Sidebar.MenuItem>
                            <Sidebar.MenuButton
                                title={t("sidebarPage.itemHelp")}
                                icon={InfoIcon}
                            />
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.Footer>
                {
                    collapseBehaviour === "icon" ?
                        <Sidebar.Rail/>
                    :
                        null
                }
            </Sidebar>
            <Sidebar.Inset>
                <InsetBar>
                    {
                        collapseBehaviour === "none" ?
                            null
                        :
                            <Sidebar.Trigger/>
                    }
                    <Text
                        variant="labelLargeSize"
                        color="high"
                    >
                        {currentTitle}
                    </Text>
                    {
                        isStateVisible ?
                            <StateNote
                                label="Sidebar.useContext().state"
                            />
                        :
                            null
                    }
                </InsetBar>
            </Sidebar.Inset>
        </Sidebar.Provider>
    </StageFrame>;

    return <Article>
        <ComponentHeader
            lead={t("sidebarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("sidebarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("sidebarPage.overviewCaption", [
                    currentTitle
                ])}
            >
                {
                    renderShell({
                        id: "docs-sidebar-overview",
                        isShortcutEnabled: true
                    })
                }
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.stageNote")}
            </Paragraph>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("sidebarPage.collapseTitle")}
            id="collapse"
        >
            <Paragraph>
                {t("sidebarPage.collapseText")}
            </Paragraph>
            <Preview>
                {
                    COLLAPSE_BEHAVIOURS.map((behaviour) => renderCell(`collapseBehaviour="${behaviour}"`, renderShell({
                        isDefaultOpen: behaviour === "none",
                        id: `docs-sidebar-${behaviour}`,
                        collapseBehaviour: behaviour,
                        isTeamVisible: false,
                        height: "short"
                    })))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("sidebarPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => renderCell(`variant="${variant}"`, renderShell({
                        id: `docs-sidebar-variant-${variant}`,
                        isTeamVisible: false,
                        height: "short",
                        variant
                    })))
                }
            </Preview>
        </Section>
        <Section
            title={t("sidebarPage.itemsTitle")}
            id="items"
        >
            <Paragraph>
                {t("sidebarPage.itemsText")}
            </Paragraph>
            <Preview
                caption={t("sidebarPage.itemsCaption", [
                    lastAction || t("sidebarPage.noAction")
                ])}
            >
                <StageFrame
                    height="short"
                >
                    <Sidebar.Provider
                        compactBreakpoint={STAGE_COMPACT_BREAKPOINT}
                        isDefaultOpen={true}
                        shortcut={null}
                    >
                        <Sidebar
                            title={t("sidebarPage.navTitle")}
                            collapseBehaviour="none"
                        >
                            <Sidebar.Content>
                                <Sidebar.Group
                                    title={t("sidebarPage.groupProjects")}
                                >
                                    <Sidebar.GroupAction
                                        onPress={handleAction(t("sidebarPage.addProject"))}
                                        title={t("sidebarPage.addProject")}
                                    />
                                    <Sidebar.Menu>
                                        <Sidebar.MenuItem>
                                            <Sidebar.MenuButton
                                                title={t("sidebarPage.projectWebsite")}
                                                icon={HomeIcon}
                                            />
                                            <Sidebar.MenuAction
                                                onPress={handleAction(t("sidebarPage.projectActions"))}
                                                title={t("sidebarPage.projectActions")}
                                                isShowOnHover={false}
                                            />
                                        </Sidebar.MenuItem>
                                        <Sidebar.MenuItem>
                                            <Sidebar.MenuButton
                                                title={t("sidebarPage.projectMobile")}
                                                icon={FileIcon}
                                            />
                                            <Sidebar.MenuBadge
                                                label={t("sidebarPage.badgeUnread")}
                                                type="danger"
                                                count={120}
                                            />
                                        </Sidebar.MenuItem>
                                        <Sidebar.MenuItem>
                                            <Sidebar.MenuButton
                                                title={t("sidebarPage.projectArchive")}
                                                isDisabled={true}
                                                icon={CheckIcon}
                                            />
                                        </Sidebar.MenuItem>
                                    </Sidebar.Menu>
                                </Sidebar.Group>
                                <Sidebar.Group
                                    title={t("sidebarPage.groupLoading")}
                                >
                                    <Sidebar.MenuSkeleton
                                        count={2}
                                    />
                                </Sidebar.Group>
                            </Sidebar.Content>
                        </Sidebar>
                        <Sidebar.Inset>
                            <InsetBar>
                                <Text
                                    variant="labelLargeSize"
                                    color="high"
                                >
                                    {t("sidebarPage.groupProjects")}
                                </Text>
                            </InsetBar>
                        </Sidebar.Inset>
                    </Sidebar.Provider>
                </StageFrame>
            </Preview>
            <CodeBlock
                code={ITEMS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.stateTitle")}
            id="state"
        >
            <Paragraph>
                {t("sidebarPage.stateText")}
            </Paragraph>
            <Preview>
                <Switch
                    onPress={handleOpenSwitch}
                    isChecked={isOpen}
                    title="isOpen"
                />
                {
                    renderShell({
                        id: "docs-sidebar-state",
                        onOpenChange: setIsOpen,
                        isStateVisible: true,
                        isTeamVisible: false,
                        height: "short",
                        isOpen
                    })
                }
            </Preview>
            <CodeBlock
                code={STATE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.boardTitle")}
            id="board"
        >
            <Paragraph>
                {t("sidebarPage.boardText")}
            </Paragraph>
            <Preview
                caption={t("sidebarPage.boardCaption", [
                    boardCurrentTitle
                ])}
            >
                <Switch
                    title={t("sidebarPage.boardExpanded")}
                    onPress={handleBoardRailSwitch}
                    isChecked={isBoardOpen}
                />
                {renderBoard()}
            </Preview>
            <CodeBlock
                code={BOARD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.inputTitle")}
            id="input"
        >
            <Paragraph>
                {t("sidebarPage.inputText")}
            </Paragraph>
            <CodeBlock
                code={INPUT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("sidebarPage.groupsText")}
            </Paragraph>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.subItemsTitle")}
            id="sub-items"
        >
            <Paragraph>
                {t("sidebarPage.subItemsText")}
            </Paragraph>
            <CodeBlock
                code={SUB_ITEM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.nestedTitle")}
            id="nested"
        >
            <Paragraph>
                {t("sidebarPage.nestedText")}
            </Paragraph>
            <Preview
                caption={t("sidebarPage.nestedCaption")}
            >
                <StageFrame
                    height="short"
                >
                    <Sidebar.Provider
                        compactBreakpoint={STAGE_COMPACT_BREAKPOINT}
                        shortcut={null}
                    >
                        <Sidebar
                            title={t("sidebarPage.navTitle")}
                            collapseBehaviour="none"
                        >
                            <Sidebar.Content>
                                <Sidebar.Group>
                                    <Sidebar.Menu>
                                        <Sidebar.MenuItem
                                            isCollapsible={false}
                                        >
                                            <Sidebar.MenuButton
                                                title={t("sidebarPage.nestedWorkspace")}
                                                icon={StarIcon}
                                            />
                                            <Sidebar.MenuSub>
                                                <Sidebar.MenuItem
                                                    isDefaultExpanded={true}
                                                >
                                                    <Sidebar.MenuButton
                                                        title={t("sidebarPage.nestedReleases")}
                                                        icon={FileIcon}
                                                        size="medium"
                                                    />
                                                    <Sidebar.MenuSub>
                                                        <Sidebar.MenuSubItem>
                                                            <Sidebar.MenuSubButton
                                                                title={t("sidebarPage.nestedNotes")}
                                                            />
                                                        </Sidebar.MenuSubItem>
                                                    </Sidebar.MenuSub>
                                                </Sidebar.MenuItem>
                                                <Sidebar.MenuItem>
                                                    <Sidebar.MenuButton
                                                        title={t("sidebarPage.nestedArchive")}
                                                        endIcon={ChevronRightIcon}
                                                        icon={InfoIcon}
                                                        size="medium"
                                                    />
                                                </Sidebar.MenuItem>
                                            </Sidebar.MenuSub>
                                        </Sidebar.MenuItem>
                                    </Sidebar.Menu>
                                </Sidebar.Group>
                            </Sidebar.Content>
                        </Sidebar>
                    </Sidebar.Provider>
                </StageFrame>
            </Preview>
            <CodeBlock
                code={NESTED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.outlineTitle")}
            id="outline"
        >
            <Paragraph>
                {t("sidebarPage.outlineText")}
            </Paragraph>
            <CodeBlock
                code={OUTLINE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sidebarPage.railBadgesTitle")}
            id="rail-badges"
        >
            <Paragraph>
                {t("sidebarPage.railBadgesText")}
            </Paragraph>
            <Paragraph>
                {t("sidebarPage.railFlyoutText")}
            </Paragraph>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("sidebarPage.a11yLandmarks"),
                    t("sidebarPage.a11yGroups"),
                    t("sidebarPage.a11yButtons"),
                    t("sidebarPage.a11yCurrent"),
                    t("sidebarPage.a11ySub"),
                    t("sidebarPage.a11yNested"),
                    t("sidebarPage.a11yRail"),
                    t("sidebarPage.a11yTrigger"),
                    t("sidebarPage.a11yShortcut"),
                    t("sidebarPage.a11yBadge"),
                    t("sidebarPage.a11yActions"),
                    t("sidebarPage.a11yPhone"),
                    t("sidebarPage.a11yMotion"),
                    t("sidebarPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("sidebarPage.structureText")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        code("Sidebar.Provider"),
                        t("sidebarPage.partProvider")
                    ],
                    [
                        code("Sidebar"),
                        t("sidebarPage.partSidebar")
                    ],
                    [
                        code("Sidebar.Header"),
                        t("sidebarPage.partHeader")
                    ],
                    [
                        code("Sidebar.Content"),
                        t("sidebarPage.partContent")
                    ],
                    [
                        code("Sidebar.Group"),
                        t("sidebarPage.partGroup")
                    ],
                    [
                        code("Sidebar.GroupLabel"),
                        t("sidebarPage.partGroupLabel")
                    ],
                    [
                        code("Sidebar.GroupContent"),
                        t("sidebarPage.partGroupContent")
                    ],
                    [
                        code("Sidebar.Input"),
                        t("sidebarPage.partInput")
                    ],
                    [
                        code("Sidebar.MenuSubItem"),
                        t("sidebarPage.partMenuSubItem")
                    ],
                    [
                        code("Sidebar.GroupAction"),
                        t("sidebarPage.partGroupAction")
                    ],
                    [
                        code("Sidebar.Menu"),
                        t("sidebarPage.partMenu")
                    ],
                    [
                        code("Sidebar.MenuItem"),
                        t("sidebarPage.partMenuItem")
                    ],
                    [
                        code("Sidebar.MenuButton"),
                        t("sidebarPage.partMenuButton")
                    ],
                    [
                        code("Sidebar.MenuBadge"),
                        t("sidebarPage.partMenuBadge")
                    ],
                    [
                        code("Sidebar.MenuAction"),
                        t("sidebarPage.partMenuAction")
                    ],
                    [
                        code("Sidebar.MenuSub"),
                        t("sidebarPage.partMenuSub")
                    ],
                    [
                        code("Sidebar.MenuSubButton"),
                        t("sidebarPage.partMenuSubButton")
                    ],
                    [
                        code("Sidebar.MenuSkeleton"),
                        t("sidebarPage.partMenuSkeleton")
                    ],
                    [
                        code("Sidebar.Separator"),
                        t("sidebarPage.partSeparator")
                    ],
                    [
                        code("Sidebar.Footer"),
                        t("sidebarPage.partFooter")
                    ],
                    [
                        code("Sidebar.Rail"),
                        t("sidebarPage.partRail")
                    ],
                    [
                        code("Sidebar.Trigger"),
                        t("sidebarPage.partTrigger")
                    ],
                    [
                        code("Sidebar.Inset"),
                        t("sidebarPage.partInset")
                    ]
                ]}
                headers={[
                    t("sidebarPage.partsName"),
                    t("sidebarPage.partsRole")
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar
                </InlineCode>
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propCollapseBehaviour"),
                        type: "\"icon\" | \"offcanvas\" | \"none\"",
                        defaultValue: "\"icon\"",
                        name: "collapseBehaviour"
                    },
                    {
                        type: "\"sidebar\" | \"floating\" | \"inset\"",
                        description: t("sidebarPage.propVariant"),
                        name: "variant"
                    },
                    {
                        description: t("sidebarPage.propSide"),
                        type: "\"left\" | \"right\"",
                        defaultValue: "\"left\"",
                        name: "side"
                    },
                    {
                        description: t("sidebarPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("sidebarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("sidebarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.Provider
                </InlineCode>
                {" "}
                {t("sidebarPage.providerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propProviderIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propProviderIsDefaultOpen"),
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propProviderOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("sidebarPage.propProviderVariant"),
                        type: "\"sidebar\" | \"floating\" | \"inset\"",
                        defaultValue: "\"sidebar\"",
                        name: "variant"
                    },
                    {
                        description: t("sidebarPage.propProviderShortcut"),
                        defaultValue: "[\"mod\", \"b\"]",
                        type: "Array<KbdKey> | null",
                        name: "shortcut"
                    },
                    {
                        description: t("sidebarPage.propProviderPersistKey"),
                        type: "string | null",
                        defaultValue: "null",
                        name: "persistKey"
                    },
                    {
                        description: t("sidebarPage.propProviderCompactBreakpoint"),
                        name: "compactBreakpoint",
                        type: "number"
                    },
                    {
                        description: t("sidebarPage.propProviderWideBreakpoint"),
                        name: "wideBreakpoint",
                        type: "number"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("sidebarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("sidebarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.providerNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuButton
                </InlineCode>
                {" "}
                {t("sidebarPage.menuButtonText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propButtonTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sidebarPage.propButtonSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("sidebarPage.propButtonIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "(props: { borderRadius, size }) => ReactNode",
                        description: t("sidebarPage.propButtonRenderIcon"),
                        name: "renderIcon"
                    },
                    {
                        description: t("sidebarPage.propButtonEndIcon"),
                        type: "NCoreUIKitIcon",
                        name: "endIcon"
                    },
                    {
                        description: t("sidebarPage.propButtonVariant"),
                        type: "\"plain\" | \"outline\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        description: t("sidebarPage.propButtonIsActive"),
                        defaultValue: "false",
                        name: "isActive",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propButtonIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propButtonSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("sidebarPage.propButtonHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("sidebarPage.propButtonTooltip"),
                        type: "ITooltipProps | string | boolean",
                        name: "tooltip"
                    },
                    {
                        description: t("sidebarPage.propButtonOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.menuButtonNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuBadge
                </InlineCode>
                {" "}
                {t("sidebarPage.menuBadgeText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propBadgeCount"),
                        type: "number",
                        name: "count"
                    },
                    {
                        description: t("sidebarPage.propBadgeMax"),
                        defaultValue: "99",
                        type: "number",
                        name: "max"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("sidebarPage.propBadgeType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("sidebarPage.propBadgeLabel"),
                        type: "string",
                        name: "label"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.Input
                </InlineCode>
                {" "}
                {t("sidebarPage.inputPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propInputAriaLabel"),
                        name: "aria-label",
                        type: "string"
                    },
                    {
                        description: t("sidebarPage.propInputPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("sidebarPage.propInputValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("sidebarPage.propInputOnChangeText"),
                        type: "(value: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("sidebarPage.propInputShortcut"),
                        type: "Array<KbdKey> | string",
                        name: "shortcut"
                    },
                    {
                        description: t("sidebarPage.propInputIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.GroupLabel
                </InlineCode>
                {", "}
                <InlineCode>
                    Sidebar.GroupContent
                </InlineCode>
                {" "}
                {t("sidebarPage.groupPartsText")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuSubItem
                </InlineCode>
                {" "}
                {t("sidebarPage.subItemPartText")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuAction
                </InlineCode>
                {" "}
                {t("sidebarPage.menuActionText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propActionTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sidebarPage.propActionIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("sidebarPage.propActionIsShowOnHover"),
                        name: "isShowOnHover",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propActionIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propActionOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.groupActionNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuItem
                </InlineCode>
                {" "}
                {t("sidebarPage.menuItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propItemIsCollapsible"),
                        name: "isCollapsible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propItemIsExpanded"),
                        name: "isExpanded",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propItemIsDefaultExpanded"),
                        name: "isDefaultExpanded",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propItemOnExpandedChange"),
                        type: "(isExpanded: boolean) => void",
                        name: "onExpandedChange"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuSubButton
                </InlineCode>
                {" "}
                {t("sidebarPage.menuSubButtonText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propSubTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sidebarPage.propSubIsActive"),
                        defaultValue: "false",
                        name: "isActive",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propSubIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propSubSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"small\"",
                        name: "size"
                    },
                    {
                        description: t("sidebarPage.propSubHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("sidebarPage.propSubOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.MenuSkeleton
                </InlineCode>
                {" "}
                {t("sidebarPage.menuSkeletonText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propSkeletonCount"),
                        defaultValue: "5",
                        type: "number",
                        name: "count"
                    },
                    {
                        description: t("sidebarPage.propSkeletonIsShowIcon"),
                        defaultValue: "true",
                        name: "isShowIcon",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propSkeletonSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.Menu
                </InlineCode>
                {" "}
                {t("sidebarPage.menuText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "Array<{ key, title, icon, count, countType, isActive, isDisabled, href, onPress, items }>",
                        description: t("sidebarPage.propMenuItems"),
                        name: "items"
                    },
                    {
                        description: t("sidebarPage.propMenuSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.Group
                </InlineCode>
                {" "}
                {t("sidebarPage.groupText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propGroupTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.Trigger
                </InlineCode>
                {" "}
                {t("sidebarPage.triggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propTriggerTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sidebarPage.propTriggerIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("sidebarPage.propTriggerOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Sidebar.Rail
                </InlineCode>
                {" "}
                {t("sidebarPage.railText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sidebarPage.propRailTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("sidebarPage.propRailOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sidebarPage.hookNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SidebarPage;
